import React, { FC } from 'react';
import classNames from 'classnames';
import format from 'date-fns/format';
import { TIME_12 } from 'src/common/constants';
import { PAYMENT_STATUS } from 'src/inbox/constants';
import type { PaymentAddon } from 'src/types/trips/PaymentAddon';
import getClassName from 'src/common/utils/getClassName';
import Button from 'src/common/components/Button';
import s from './PaymentAddon.module.scss';

type Colors = 'neutral-25' | 'neutral-100' | 'feedback-warning-50' | 'feedback-info-50';

type Label = PaymentAddon['status'] | 'paid';

export type PaymentAddonProps = {
  status: Label;
  paymentAmount: string;
  paymentCategory: string;
  paymentLink: string;
  expiryDate: string;
  lastActivityDate: string;
  userRole: 'owner' | 'renter';
  /** Link to the owner add-ons gmb-next /charge/add-on/ page */
  summaryLink?: string;
};

const colorMap: Record<Label, Colors> = {
  confirmed: 'feedback-info-50',
  canceled: 'neutral-100',
  expired: 'feedback-warning-50',
  pending: 'feedback-info-50',
  refunded: 'feedback-info-50',
  paid: 'feedback-info-50',
};

const PaymentAddon: FC<PaymentAddonProps> = ({
  status,
  paymentAmount,
  paymentCategory,
  expiryDate,
  lastActivityDate,
  userRole,
  summaryLink,
  paymentLink,
}) => {
  const formattedExpiryDate = format(new Date(expiryDate), 'dd/MM/yy');
  const formattedLastActivityDate = format(new Date(lastActivityDate), `dd MMM yyyy ${TIME_12}`);
  const Root = summaryLink ? 'a' : 'div';

  const label = status === 'confirmed' ? 'Paid' : status;
  return (
    <Root
      className={getClassName(s, 'root', classNames(colorMap[status], userRole))}
      href={summaryLink}
    >
      <ul className={getClassName(s, 'list', 'price')}>
        <li className={s.paymentCategory}>{paymentCategory}</li>
        <li className={s.paymentAmount}>{paymentAmount}</li>
      </ul>
      <ul className={s.list}>
        <li className={s.status}>
          {status === PAYMENT_STATUS.PENDING && userRole === 'renter' ? (
            <Button
              href={paymentLink}
              className={s.button}
              classNameModifier="small"
            >
              Pay
            </Button>
          ) : (
            label
          )}
        </li>
        <li className={s.date}>
          <time dateTime={userRole === 'owner' ? lastActivityDate : expiryDate}>
            {userRole === 'owner'
              ? formattedLastActivityDate
              : `Expires ${formattedExpiryDate}`}
          </time>
        </li>
      </ul>
    </Root>
  );
};

export default PaymentAddon;
